import { Finder, Pagination } from '@ekkogmbh/apisdk';
import { Add, Delete, Edit } from '@mui/icons-material';
import { useCallback, useState } from 'react';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import { DataTable, dataTableHighlight } from 'src/Common/Components/DataTable';
import Permissions from 'src/Common/Permissions';
import { FinderPanel } from './FinderPanel';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import CompartmentSelectorChip from 'src/CompartmentSelectorManagement/Components/CompartmentSelectorChip';
import { useContentControl } from 'src/Common/Providers/ContentControlProvider';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import useNavigation from 'src/Common/Providers/NavigationProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';
import ScrollableStack from 'src/Common/Components/ScrollableStack';

const ROW_MIN_HEIGHT = 64;

export const FinderManagementContent = (): React.JSX.Element => {
  const { api } = useEkko();
  const { scrollTop } = useNavigation();
  const { refresh } = usePagination();
  const { setPanelOpen, togglePanel } = useContentControl();

  const [isProcessing, startProcessing] = useRequest();
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [selectedFinder, setSelectedFinder] = useState<Finder>();

  const dismiss = () => {
    setDialogOpen(false);
    setPanelOpen(false);
    setSelectedFinder(undefined);
  };

  const deleteFinder = useCallback(() => {
    const { name, coordinate } = selectedFinder!;
    startProcessing(
      () => api.deleteFinder(coordinate, name),
      () => {
        dismiss();
        refresh();
      },
    );
  }, [selectedFinder]);

  const fetchItems = useCallback((pagination: Pagination) => api.getFinders(pagination), [api]);

  return (
    <ContentControl
      panel={<FinderPanel finder={selectedFinder} closeHandler={dismiss} />}
      dialog={
        <GenericDialog
          type="confirmation"
          maxWidth={'sm'}
          fullWidth={true}
          centered={true}
          open={isDialogOpen}
          title={'Delete Finder'}
          text={selectedFinder?.name ?? ''}
          onClose={dismiss}
          onConfirm={deleteFinder}
        />
      }
      buttons={[
        <ContentControlButton
          key={'add'}
          content={<Add />}
          tooltip={'Add Finder'}
          onClick={() => {
            scrollTop();
            setSelectedFinder(undefined);
            togglePanel();
          }}
          requiredPermission={Permissions.FINDERS_WRITE}
        />,
      ]}
    >
      <DataTable
        actions={[
          {
            title: 'Edit',
            icon: <Edit />,
            disabled: isProcessing,
            onClick: (entry: Finder) => {
              dismiss();
              setSelectedFinder(entry);
              scrollTop();
              setPanelOpen(true);
            },
          },
          {
            title: 'Delete',
            icon: <Delete />,
            disabled: isProcessing,
            onClick: (entry: Finder) => {
              dismiss();
              setDialogOpen(true);
              setSelectedFinder(entry);
            },
          },
        ]}
        columns={[
          {
            header: 'Name',
            body: (entry: Finder, searchText?: string) => (
              <ScrollableStack direction={'row'} width={'100%'} minHeight={ROW_MIN_HEIGHT} alignItems={'center'}>
                {searchText ? dataTableHighlight(entry.name, [searchText]) : entry.name}
              </ScrollableStack>
            ),
            filterField: 'name',
            sortField: 'F.name',
            proportionalContent: '25%',
          },
          {
            header: 'Coordinate',
            body: (entry: Finder, searchText?: string) => (
              <ScrollableStack direction={'row'} width={'100%'} minHeight={ROW_MIN_HEIGHT} alignItems={'center'}>
                {searchText ? dataTableHighlight(entry.coordinate, [searchText]) : entry.coordinate}
              </ScrollableStack>
            ),
            filterField: 'coordinate',
            sortField: 'N.materializedPath',
            proportionalContent: '25%',
          },
          {
            header: 'Notification-Strategy',
            body: (entry: Finder) => entry.notificationStrategy.name,
            fitContent: true,
          },
          {
            header: 'Template-Names',
            body: (entry: Finder, searchText?: string) => {
              const text = entry.templateNames ? entry.templateNames.join(', ') : '-';
              return (
                <ScrollableStack direction={'row'} width={'100%'} minHeight={ROW_MIN_HEIGHT} alignItems={'center'}>
                  {searchText ? dataTableHighlight(text, [searchText]) : text}
                </ScrollableStack>
              );
            },
            proportionalContent: '35%',
          },
          {
            header: 'Compartment-Selector',
            body: (entry: Finder, searchText?: string) => {
              const { compartmentSelector } = entry;
              if (!compartmentSelector) {
                return '-';
              }
              return <CompartmentSelectorChip compartmentSelector={compartmentSelector} highlightText={searchText} />;
            },
            fitContent: true,
          },
          {
            header: 'Input-Instructions',
            body: (entry: Finder, searchText?: string) => {
              const text = entry.compartmentInputInstructions ?? '-';
              return (
                <ScrollableStack direction={'row'} width={'100%'} minHeight={ROW_MIN_HEIGHT} alignItems={'center'}>
                  {searchText ? dataTableHighlight(text, [searchText]) : text}
                </ScrollableStack>
              );
            },
            proportionalContent: '15%',
          },
        ]}
        fetchItems={fetchItems}
      />
    </ContentControl>
  );
};
